import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { apiClient } from '../../api_actions';
import { orbitalSpinner } from '../LoadingOrbitalSpinner/LoadingOrbitalSpinner';
import { chartTheme } from '../../constants/chartTheme';

const baseChartConfig = {
    axes: [
        {
            type: 'category',
            position: 'bottom',
        },
        {
            type: 'number',
            position: 'left',
        },
    ],
    legend: {
        enabled: true,
    },
    animation: {
        enabled: true,
        duration: 600,
    },
};

// Separate utility function
const formatSiteName = (siteName) => {
    // Replace dots with unicode character U+2024 (One Dot Leader)
    return siteName.replace(/\./g, '\u2024');
};

// Separate data transformation
const transformSiteEventsData = (rawData) => {
    // Get all unique site names (excluding 'date' key)
    const siteNames = [];
    rawData.forEach(dateRow => {
        Object.keys(dateRow).forEach(key => {
            if (key !== 'date' && !siteNames.includes(key)) {
                siteNames.push(key);
            }
        });
    });

    // Convert Unix timestamps (seconds) to MM/DD/YYYY strings and sort by date
    const finalData = rawData
        .slice()
        .sort((a, b) => a.date - b.date)
        .map(dateRow => {
            const formatted = dayjs.unix(dateRow.date).format('MM/DD/YYYY');
            const newRow = { date: formatted };
            // Replace dots in keys with unicode character
            Object.keys(dateRow).forEach(key => {
                if (key !== 'date') {
                    newRow[formatSiteName(key)] = dateRow[key];
                }
            });
            return newRow;
        });

    // Calculate total events per site for sorting
    const siteTotals = {};
    siteNames.forEach(siteName => {
        siteTotals[siteName] = rawData.reduce((sum, row) => sum + (Number(row[siteName]) || 0), 0);
    });

    // Sort sites by total events descending so legend reflects highest-volume sites first
    const sortedSiteNames = [...siteNames].sort((a, b) => siteTotals[b] - siteTotals[a]);

    // Create series for each site in sorted order
    const series = sortedSiteNames.map(siteName => ({
        type: 'line',
        xKey: 'date',
        xName: 'Date',
        yKey: formatSiteName(siteName),
        yName: formatSiteName(siteName),
        interpolation: {
            type: 'smooth',
        },
    }));

    return { data: finalData, series };
};

// Separate API call
const fetchSiteEventsData = async (startAndEndUnixDatesParams = '') => {
    try {
        const res = await apiClient.post(`read/dashboardlinecharts?${startAndEndUnixDatesParams()}`);
        if (res && Array.isArray(res)) {
            return transformSiteEventsData(res);
        } else if (res && Array.isArray(res.data)) {
            return transformSiteEventsData(res.data);
        }
        return { data: [], series: [] };
    } catch (error) {
        console.error('Failed to load data:', error);
        return { data: [], series: [] };
    }
};

// Factory function - simplified
const createSiteEventsConfig = (data = [], series = [], formatDateRange = () => '') => ({
    ...baseChartConfig,
    theme: chartTheme,
    series: series,
    title: { text: 'Events by Site' },
    subtitle: { text: formatDateRange() },
    data: data,
    overlays: {
        loading: {
            renderer: orbitalSpinner,
        },
    },
});

export const EventsBySiteChartLogic = (startAndEndUnixDatesParams = '', formatDateRange = () => '') => {
    const [siteEventsConfig, setSiteEventsConfig] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            const result = await fetchSiteEventsData(startAndEndUnixDatesParams);
            setSiteEventsConfig(createSiteEventsConfig(result.data, result.series, formatDateRange));
            setLoading(false);
        };

        loadData();
    }, [startAndEndUnixDatesParams, formatDateRange]);

    return {
        chartConfig: siteEventsConfig,
        loading,
    };
};
